import { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { LeadForm } from '../components/forms/LeadForm';
import { Calendar, Clock, MapPin, User } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const QR_CODE = "https://customer-assets.emergentagent.com/job_simple-realty-hub/artifacts/g44ovk6k_attachment_3.jpeg";

export const OpenHouses = () => {
  const [openHouses, setOpenHouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedHouse, setSelectedHouse] = useState(null);

  useEffect(() => {
    fetchOpenHouses();
  }, []);

  const fetchOpenHouses = async () => {
    try {
      const response = await axios.get(`${API}/open-houses`);
      setOpenHouses(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching open houses:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="open-houses-page">
      {/* Hero Section */}
      <section className="py-24 bg-[#0a0a0a]" data-testid="open-houses-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <p className="section-overline">Open Houses</p>
            <h1 className="text-4xl sm:text-5xl font-light text-[#F5F5F0] mt-4 mb-6" style={{ fontFamily: 'Playfair Display' }}>
              Visit Our <span className="text-gold-gradient">Open Houses</span>
            </h1>
            <p className="text-lg text-[#808080] max-w-2xl mx-auto">
              Experience properties in person. Browse our upcoming open houses and schedule your visit today.
            </p>
          </div>
        </div>
      </section>

      {/* QR Code Sign-In Section */}
      <section className="py-16 bg-[#111111]" data-testid="qr-signin">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="text-center lg:text-left">
              <p className="section-overline">Quick Sign-In</p>
              <h2 className="section-title mt-2 mb-6">Scan to Register</h2>
              <p className="text-[#808080] mb-6">
                Visiting an open house? Scan the QR code to quickly register and receive property updates, market insights, and exclusive listings.
              </p>
              <img 
                src={QR_CODE} 
                alt="Open House QR Code" 
                className="w-48 h-48 mx-auto lg:mx-0 rounded-lg"
              />
            </div>
            <LeadForm 
              source="open_house"
              title="Open House Sign-In"
              subtitle="Register for open house updates and property alerts."
              buttonText="Sign In"
              showMessage={false}
            />
          </div>
        </div>
      </section>

      {/* Open Houses List */}
      <section className="py-24 bg-[#0a0a0a]" data-testid="open-houses-list">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <p className="section-overline">Upcoming Events</p>
            <h2 className="section-title mt-2">Scheduled Open Houses</h2>
          </div>

          {loading ? (
            <div className="flex justify-center">
              <div className="spinner" />
            </div>
          ) : openHouses.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-[#808080] text-lg mb-6">No open houses currently scheduled.</p>
              <p className="text-[#F5F5F0]">Check back soon or contact us for private showings.</p>
              <a href="https://calendly.com/seantherealtor/15min" target="_blank" rel="noopener noreferrer">
                <Button className="btn-gold mt-6">
                  Schedule Private Showing
                </Button>
              </a>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {openHouses.map((house) => (
                <div 
                  key={house.id} 
                  className="card-hover bg-[#111111] rounded-lg overflow-hidden"
                  data-testid={`open-house-card-${house.id}`}
                >
                  {house.image_url && (
                    <img 
                      src={house.image_url} 
                      alt={house.property_address}
                      className="w-full h-48 object-cover"
                    />
                  )}
                  <div className="p-6">
                    <h3 className="text-xl font-light text-[#F5F5F0] mb-2" style={{ fontFamily: 'Playfair Display' }}>
                      {house.property_address}
                    </h3>
                    <p className="text-[#808080] text-sm mb-4">
                      {house.city}, {house.state} {house.zip_code}
                    </p>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center text-[#F5F5F0]">
                        <Calendar className="w-4 h-4 text-[#D4AF37] mr-2" />
                        {house.date}
                      </div>
                      <div className="flex items-center text-[#F5F5F0]">
                        <Clock className="w-4 h-4 text-[#D4AF37] mr-2" />
                        {house.start_time} - {house.end_time}
                      </div>
                      {house.agent_name && (
                        <div className="flex items-center text-[#F5F5F0]">
                          <User className="w-4 h-4 text-[#D4AF37] mr-2" />
                          {house.agent_name}
                        </div>
                      )}
                    </div>
                    <Button 
                      className="btn-outline-gold w-full mt-4"
                      onClick={() => setSelectedHouse(house)}
                    >
                      Register to Attend
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Registration Modal */}
      {selectedHouse && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="bg-[#111111] p-8 rounded-lg max-w-md w-full mx-4 border border-[#D4AF37]/20">
            <h3 className="text-xl font-light text-[#F5F5F0] mb-4" style={{ fontFamily: 'Playfair Display' }}>
              Register for Open House
            </h3>
            <p className="text-[#808080] text-sm mb-4">
              {selectedHouse.property_address}, {selectedHouse.city}
            </p>
            <LeadForm 
              source="open_house"
              title=""
              buttonText="Register"
              showMessage={false}
              openHouseId={selectedHouse.id}
            />
            <Button 
              variant="ghost" 
              className="w-full mt-4 text-[#808080]"
              onClick={() => setSelectedHouse(null)}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
